import React, { useState } from "react";
import { useAppSelector } from "../../store/hooks";
import type { ReactionGroup } from "../../services/rtkApi/agentApi";
import { ReactionDetails } from "./ReactionDetails";

interface MessageReactionsProps {
  /** reaction groups for this message (may be empty) */
  reactions: ReactionGroup[];
  /** toggle the current user's reaction with this emoji on/off */
  onToggle: (emoji: string) => void;
  /** align with the bubble — self messages are right-aligned */
  align?: "start" | "end";
}

/**
 * Reaction chip row under a message bubble. Each chip shows the emoji + count, highlights when
 * the current user reacted, and carries a 🤖 badge when sitebot reacted. Tapping ANY chip (or
 * the add button) opens the details popover — which shows who reacted and is the ONLY place a
 * reaction is toggled. That deliberate split means tapping a chip to "see who" can never
 * silently remove a reaction.
 */
export function MessageReactions({ reactions, onToggle, align = "start" }: MessageReactionsProps) {
  const [open, setOpen] = useState(false);
  const me = useAppSelector((s) => s.auth.username) || "";

  const sorted = [...reactions].sort(
    (a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji),
  );

  return (
    <div
      className={`group relative flex flex-wrap items-center gap-1 mt-1 ${
        align === "end" ? "justify-end pr-1" : "justify-start pl-1"
      }`}
    >
      {sorted.map((r) => (
        <button
          key={r.emoji}
          type="button"
          title="see who reacted"
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 leading-none transition-colors ${
            r.mine
              ? "border-sky bg-sky/15 font-semibold"
              : "border-ink/15 bg-white hover:bg-gray-50"
          }`}
          style={{ boxShadow: "1px 1px 0px rgba(0,0,0,0.08)" }}
        >
          <span className="text-sm leading-none">{r.emoji}</span>
          {r.count > 1 && (
            <span className="text-[10px] text-gray-500">{r.count}</span>
          )}
          {r.agentReacted && (
            <span title="sitebot reacted" className="text-[10px] leading-none">
              🤖
            </span>
          )}
        </button>
      ))}

      <button
        type="button"
        aria-label="add reaction"
        onClick={() => setOpen(true)}
        className={`flex h-6 w-6 items-center justify-center rounded-full border border-ink/15 bg-white text-[15px] leading-none text-gray-400 transition hover:border-ink/40 hover:text-gray-700 ${
          sorted.length > 0 ? "" : "opacity-50 group-hover:opacity-100 focus:opacity-100"
        }`}
      >
        +
      </button>

      {open && (
        <ReactionDetails
          reactions={sorted}
          onToggle={onToggle}
          onClose={() => setOpen(false)}
          align={align}
          me={me}
        />
      )}
    </div>
  );
}
